// Reports domain overrides (gap R8): Register Records page.
//  - legacy RegisterRecordListComponent column set (cash / cheque / credit card as
//    total / submitted, gift card, other, expenses, return orders, refunds — only once closed)
//  - `sales_sum_grand_total` is the withSum('sales','grand_total') of the session's sales
//    (labelled as such, not as a closing amount)
//  - "View" row action → register detail (GET pos/register/{id}, PosRegisterComponent)

import { createElement } from 'react';
import { Eye } from 'iconsax-reactjs';

import { t } from '../../i18n';
import { RegisterDetail } from '../../features/reports/RegisterDetail';
import { cashDifference, moneyValue, relationName } from '../configHelpers';
import type { CrudColumn, CrudRecord, CrudResourceConfig } from '../types';

const closedOnly = (fn: (row: CrudRecord) => string) => (row: CrudRecord) => (row.closed_at ? fn(row) : '');
const totalSubmitted = (total: string, submitted: string) => closedOnly((row) => `${moneyValue(row[total] ?? 0)} / ${moneyValue(row[submitted] ?? 0)}`);
const closedMoney = (key: string) => closedOnly((row) => moneyValue(row[key] ?? 0));

const registerColumns: CrudColumn[] = [
  { key: 'created_at', label: 'Opened', sortable: true },
  { key: 'register', label: 'Register', render: (row) => `${relationName(row.register)} · ${relationName(row.location)}` },
  { key: 'register_imei', label: 'IMEI', render: (row) => String((row.register as Record<string, unknown> | undefined)?.imei ?? '-') },
  { key: 'user', label: 'User', render: (row) => relationName(row.user) },
  { key: 'cash_in_hand', label: 'Opening Cash', align: 'right', render: (row) => moneyValue(row.cash_in_hand) },
  { key: 'sales_sum_grand_total', label: 'Sales Total', align: 'right', render: (row) => moneyValue(row.sales_sum_grand_total ?? 0) },
  { key: 'total_cash', label: 'Cash Sales', align: 'right', render: totalSubmitted('total_cash_amount', 'total_cash_submitted') },
  { key: 'total_cheque', label: 'Cheque Sales', align: 'right', render: totalSubmitted('total_cheques_amount', 'total_cheques_submitted') },
  { key: 'total_cc', label: 'Card Sales', align: 'right', render: totalSubmitted('total_cc_slips_amount', 'total_cc_slips_submitted') },
  { key: 'total_gift_card_amount', label: 'Gift Card Sales', align: 'right', render: closedMoney('total_gift_card_amount') },
  { key: 'total_other_amount', label: 'Other Sales', align: 'right', render: closedMoney('total_other_amount') },
  { key: 'total_expenses_amount', label: 'Expenses', align: 'right', render: closedMoney('total_expenses_amount') },
  { key: 'total_return_orders_amount', label: 'Return Orders', align: 'right', render: closedMoney('total_return_orders_amount') },
  { key: 'total_refunds_amount', label: 'Refunds', align: 'right', render: closedMoney('total_refunds_amount') },
  {
    key: 'cash_difference',
    label: 'Cash Difference',
    align: 'right',
    render: (row) => {
      if (!row.closed_at) return '';
      const difference = cashDifference(row);
      return difference == null ? '-' : `${difference > 0 ? '+' : ''}${moneyValue(difference)}`;
    },
    tone: (row) => {
      if (!row.closed_at) return undefined;
      const difference = cashDifference(row);
      if (difference == null) return undefined;
      if (Math.abs(difference) < 0.01) return 'success';
      return Math.abs(difference) >= 20 ? 'error' : 'warning';
    }
  },
  { key: 'closed_at', label: 'Closed', sortable: true },
  { key: 'closed_by_user', label: 'Closed By', render: (row) => relationName(row.closed_by_user ?? row.closedByUser) }
];

export const overrides: Record<string, (base: CrudResourceConfig | undefined) => CrudResourceConfig> = {
  'reports/registers': (base) => ({
    ...(base as CrudResourceConfig),
    columns: registerColumns,
    pageSizes: [10, 25, 50, 100],
    rowTone: (row) => (row.closed_at ? undefined : 'success'),
    rowActions: [
      ...((base?.rowActions ?? []).filter((action) => action.key !== 'viewRegister')),
      {
        key: 'viewRegister',
        label: 'registerRecords.viewDetails',
        icon: Eye,
        run: (row, ctx) => {
          ctx.openDialog(() => createElement(RegisterDetail, { row }), { title: t('registerRecords.detailsTitle'), maxWidth: 'sm' });
        }
      }
    ]
  })
};
